import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { JumpGameSession, type JumpSessionStatus } from '../../game/jump/jumpSession';
import {
  buildTutorialJumpChart,
  getJumpTutorialOverlays,
  type JumpTutorialOverlay,
} from '../../game/jump/tutorialJumpChart';
import { markTutorialSeen } from '../../game/tutorialFlag';
import { saveModePref } from '../../game/modePref';
import { modePath } from '../../game/modeRouting';
import '../play.css';
import './jumpTutorial.css';

const TUTORIAL_CHART = buildTutorialJumpChart();

/**
 * Beats-agnostic pause after a tutorial death before it loops back to the
 * start. Long enough to read the death FX + "not quite" card, short enough that
 * a repeat attempt feels instant — the chart is only ~22 beats, so failure is
 * cheap and the player keeps trying the same gate until the right press clicks.
 */
const TUTORIAL_AUTO_RESTART_MS = 950;

function pickOverlay(overlays: JumpTutorialOverlay[], beat: number): JumpTutorialOverlay {
  for (let i = overlays.length - 1; i >= 0; i -= 1) {
    const o = overlays[i];
    if (beat >= o.fromBeat && beat < o.untilBeat) {
      return o;
    }
  }
  return overlays[0];
}

/**
 * Quick onboarding for Jump mode. Runs a hand-crafted teaching chart through
 * {@link JumpGameSession} so a brand-new player learns the "tap-to-jump,
 * hold-to-auto-hop" rhythm in a controlled environment before being dropped
 * into the osu-derived song catalogue.
 *
 * Mirrors the Waves tutorial's beat-synced overlay pattern (each lesson chunk
 * advances when the player's beat crosses a threshold).
 */
export function JumpTutorialPage() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sessionRef = useRef<JumpGameSession | null>(null);
  const [needsTap, setNeedsTap] = useState(true);
  const [status, setStatus] = useState<JumpSessionStatus>('idle');
  const isTouch = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches,
    [],
  );
  const overlays = useMemo(() => getJumpTutorialOverlays(isTouch), [isTouch]);
  const [overlay, setOverlay] = useState<JumpTutorialOverlay>(overlays[0]);

  useEffect(() => {
    saveModePref('jump');
  }, []);

  useEffect(() => {
    return () => {
      if (sessionRef.current) {
        void sessionRef.current.destroy();
        sessionRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (status === 'idle') {
      return undefined;
    }
    let raf = 0;
    const loop = () => {
      const session = sessionRef.current;
      if (session) {
        const beat = session.getCurrentBeat();
        if (Number.isFinite(beat)) {
          const next = pickOverlay(overlays, beat);
          setOverlay((cur) => (cur.fromBeat === next.fromBeat ? cur : next));
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [overlays, status]);

  const startTutorial = useCallback(async () => {
    if (!canvasRef.current) {
      return;
    }
    setNeedsTap(false);
    if (sessionRef.current) {
      await sessionRef.current.destroy();
      sessionRef.current = null;
    }
    const session = new JumpGameSession({
      chart: TUTORIAL_CHART,
      canvas: canvasRef.current,
      audioOffsetMs: 0,
      onStatusChange: (s) => setStatus(s),
      onComplete: () => {
        markTutorialSeen('jump');
      },
    });
    sessionRef.current = session;
    try {
      await session.start();
    } catch (err) {
      console.error('Failed to start Jump tutorial:', err);
      // Fall back to the start overlay; clear any prior 'failed' so the
      // auto-restart card doesn't linger behind it.
      setStatus('idle');
      setNeedsTap(true);
    }
  }, []);

  // Auto-restart on death: the chart is short, so a miss just loops you
  // straight back to retry the same gate — keep dying and you pass the instant
  // you use the right press. Only failures retry; a clear shows the ready card.
  useEffect(() => {
    if (status !== 'failed') {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      void startTutorial();
    }, TUTORIAL_AUTO_RESTART_MS);
    return () => window.clearTimeout(timer);
  }, [status, startTutorial]);

  useEffect(() => {
    if (!needsTap) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (
        e.key === ' ' ||
        e.code === 'Space' ||
        e.key === 'Enter' ||
        e.code === 'KeyW' ||
        e.code === 'KeyJ' ||
        e.code === 'ArrowUp'
      ) {
        e.preventDefault();
        void startTutorial();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [needsTap, startTutorial]);

  const skipTutorial = () => {
    markTutorialSeen('jump');
    navigate(modePath('jump'));
  };

  return (
    <div className="play-page tutorial-page" data-testid="jump-tutorial-page">
      <div className="play-canvas-wrap">
        <canvas
          ref={canvasRef}
          className="play-canvas"
          data-testid="jump-tutorial-canvas"
          onClick={() => {
            if (needsTap) {
              void startTutorial();
            }
          }}
        />

        {needsTap && (
          <div className="play-overlay" data-testid="jump-tutorial-start-overlay">
            <div className="play-overlay-card">
              <h2>Welcome to Jump</h2>
              <JumpTutorialDemo />
              <p>
                A quick{' '}
                {isTouch ? (
                  'tap'
                ) : (
                  <>
                    tap of <kbd>Space</kbd> (or <kbd>W</kbd> / <kbd>J</kbd> / <kbd>↑</kbd>)
                  </>
                )}{' '}
                gives a short hop; hold it for a full, high jump. You&apos;ll need both — and one
                touch on a spike (or a ceiling) ends the run.
              </p>
              <p className="muted faint">Short tutorial &middot; headphones recommended.</p>
              <div className="play-overlay-actions">
                <button
                  type="button"
                  className="btn btn-primary btn-large"
                  onClick={() => void startTutorial()}
                  data-testid="jump-tutorial-start"
                >
                  Start tutorial
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={skipTutorial}
                  data-testid="jump-tutorial-skip"
                >
                  Skip &mdash; take me to the songs
                </button>
              </div>
            </div>
          </div>
        )}

        {status === 'failed' && (
          <div
            className="play-overlay jump-tutorial-retry-overlay"
            data-testid="jump-tutorial-retry-overlay"
          >
            <div className="play-overlay-card jump-tutorial-retry-card">
              <h2>Not quite</h2>
              <p>Watch the prompt for each gate — short tap or long hold. Restarting…</p>
            </div>
          </div>
        )}

        {status === 'completed' && (
          <div className="play-overlay" data-testid="jump-tutorial-complete-overlay">
            <div className="play-overlay-card">
              <h2>You&apos;re ready</h2>
              <p>
                Nice — you&apos;ve got both heights. Pick a Normal-tier track to start collecting
                stars; every chart has a 1-dot Normal you can warm up on.
              </p>
              <div className="play-overlay-actions">
                <Link to={modePath('jump')} className="btn btn-primary btn-large">
                  Browse tracks
                </Link>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setNeedsTap(true);
                    setStatus('idle');
                  }}
                >
                  Replay tutorial
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {!needsTap && status !== 'completed' && status !== 'failed' && (
        <div className="tutorial-overlay" data-testid="jump-tutorial-overlay-text">
          <div className="tutorial-overlay-card">
            <h3>{overlay.title}</h3>
            <p>{overlay.body}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Looped SVG demo shown above "Start tutorial". A cube clears a low spike with
 * a shallow short hop (a quick TAP), then a tall spike with the full high jump
 * (a HOLD) — the two heights this tutorial teaches, in the shape the live
 * engine produces. Animation lives in `jumpTutorial.css` keyframes.
 */
function JumpTutorialDemo() {
  return (
    <svg
      viewBox="0 0 320 130"
      className="jump-tutorial-demo"
      role="img"
      aria-label="Demo: a quick tap makes a small hop over a low spike, then a hold makes a big jump over a tall spike"
      data-testid="jump-tutorial-demo"
    >
      <defs>
        <linearGradient id="jt-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0a1424" />
          <stop offset="1" stopColor="#10243a" />
        </linearGradient>
        <linearGradient id="jt-cube" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#b6ffd9" />
          <stop offset="1" stopColor="#3bff9d" />
        </linearGradient>
      </defs>
      <rect width="320" height="130" fill="url(#jt-bg)" />

      <path d="M0,104 L320,104" className="jump-tutorial-demo-ground" />

      {/* Low spike (short hop) then a taller spike (big jump). */}
      <polygon points="100,104 110,80 120,104" className="jump-tutorial-demo-spike" />
      <polygon points="198,104 210,44 222,104" className="jump-tutorial-demo-spike" />

      <path
        d="M16,96 L86,96 Q110,46 134,96 L186,96 Q210,-30 236,96 L304,96"
        className="jump-tutorial-demo-trail"
      />

      <g className="jump-tutorial-demo-player">
        <rect x="-9" y="-9" width="18" height="18" rx="3" fill="url(#jt-cube)" stroke="#ffffff" strokeWidth="1.4" />
      </g>

      <text className="jump-tutorial-demo-hit jump-tutorial-demo-hit-tap" x="110" y="62" textAnchor="middle">
        TAP
      </text>
      <text className="jump-tutorial-demo-hit jump-tutorial-demo-hit-hold" x="210" y="24" textAnchor="middle">
        HOLD
      </text>
    </svg>
  );
}
